import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Globe, ChevronDown, LogOut, LayoutDashboard, ShoppingCart } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { useCart } from "@/lib/cart";

export default function Header() {
  const { t, lang, setLang } = useI18n();
  const { user, logout } = useAuth();
  const { count: cartCount } = useCart();
  const [open, setOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);
  const navigate = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    if (!moreOpen) return;
    const onDown = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [moreOpen]);

  const moreItems = [
    { to: "/services", label: t("nav.services") || "Services" },
    { to: "/about", label: t("nav.about") || "About" },
    { to: "/contact", label: t("nav.contact") },
    { to: "/blog", label: t("nav.blog") },
  ];
  const moreActive = moreItems.some((m) => loc.pathname.startsWith(m.to));

  const link = (to, label, testid) => (
    <Link
      key={to}
      to={to}
      onClick={() => setOpen(false)}
      data-testid={testid}
      className={`px-3 py-2 text-sm font-medium transition-colors hover:text-emerald-700 ${
        loc.pathname === to ? "text-emerald-700" : "text-slate-600"
      }`}
    >
      {label}
    </Link>
  );

  return (
    <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-slate-200" data-testid="site-header">
      <div className="nc-container flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
          <img src="/nakshacart-logo.png" alt="NakshaCart" className="w-9 h-9 object-contain" width="36" height="36" />
          <span className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>NakshaCart</span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {link("/", t("nav.home"), "nav-home")}
          {link("/plans", t("nav.plans"), "nav-plans")}
          {link("/build", t("nav.build") || "Build a home", "nav-build")}
          {link("/pricing", t("nav.pricing"), "nav-pricing")}
          {link("/customize", t("nav.customize"), "nav-customize")}

          <div className="relative" ref={moreRef}>
            <button
              type="button"
              onClick={() => setMoreOpen((v) => !v)}
              data-testid="nav-more"
              aria-haspopup="menu"
              aria-expanded={moreOpen}
              className={`px-3 py-2 text-sm font-medium transition-colors hover:text-emerald-700 inline-flex items-center gap-1 ${
                moreActive || moreOpen ? "text-emerald-700" : "text-slate-600"
              }`}
            >
              {t("nav.more") || "More"}
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${moreOpen ? "rotate-180" : ""}`} />
            </button>
            {moreOpen && (
              <div
                role="menu"
                className="absolute right-0 mt-2 w-44 bg-white border border-slate-200 rounded-xl nc-soft-shadow py-1.5 z-50"
                data-testid="nav-more-menu"
              >
                {moreItems.map((m) => (
                  <Link
                    key={m.to}
                    to={m.to}
                    onClick={() => setMoreOpen(false)}
                    role="menuitem"
                    data-testid={`nav-more-${m.to.replace("/", "")}`}
                    className={`block px-4 py-2 text-sm transition-colors hover:bg-emerald-50 hover:text-emerald-700 ${
                      loc.pathname.startsWith(m.to) ? "text-emerald-700 font-medium" : "text-slate-700"
                    }`}
                  >
                    {m.label}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/cart"
            data-testid="header-cart-btn"
            aria-label="cart"
            className="relative p-2 rounded-full hover:bg-slate-100 transition"
          >
            <ShoppingCart className="w-5 h-5 text-slate-700" />
            {cartCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-emerald-600 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center"
                    data-testid="cart-badge">
                {cartCount}
              </span>
            )}
          </Link>

          <button
            onClick={() => setLang(lang === "en" ? "hi" : "en")}
            data-testid="lang-toggle"
            className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 text-sm font-medium hover:bg-slate-50 transition"
          >
            <Globe className="w-4 h-4" />
            {lang === "en" ? "EN" : "हिंदी"}
            <ChevronDown className="w-3 h-3 opacity-50" />
          </button>

          {user ? (
            <div className="hidden md:flex items-center gap-2">
              <button
                onClick={() => navigate("/dashboard")}
                className="nc-btn-secondary text-sm py-2"
                data-testid="header-dashboard-btn"
              >
                <LayoutDashboard className="w-4 h-4" /> {t("nav.dashboard")}
              </button>
              <button
                onClick={async () => { await logout(); navigate("/"); }}
                className="text-sm text-slate-500 hover:text-slate-700 px-2"
                data-testid="header-logout-btn"
                aria-label="logout"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="hidden md:flex items-center gap-2">
              <Link
                to="/partner/register"
                className="text-sm font-medium text-slate-600 hover:text-emerald-700 px-3 py-2 rounded-full border border-transparent hover:border-emerald-200 hover:bg-emerald-50 transition"
                data-testid="header-become-partner-btn"
              >
                {t("nav.becomePartner")}
              </Link>
              <Link to="/login" className="nc-btn-secondary text-sm py-2" data-testid="header-login-btn">{t("nav.login")}</Link>
              <Link to="/signup" className="nc-btn-primary text-sm py-2" data-testid="header-signup-btn">{t("nav.signup")}</Link>
            </div>
          )}

          <button
            onClick={() => setOpen(!open)}
            className="md:hidden p-2"
            data-testid="mobile-menu-toggle"
            aria-label="menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-slate-200 bg-white" data-testid="mobile-menu">
          <div className="nc-container py-4 flex flex-col gap-1">
            {link("/", t("nav.home"), "mobile-nav-home")}
            {link("/plans", t("nav.plans"), "mobile-nav-plans")}
            {link("/build", t("nav.build") || "Build a home", "mobile-nav-build")}
            {link("/pricing", t("nav.pricing"), "mobile-nav-pricing")}
            {link("/customize", t("nav.customize"), "mobile-nav-customize")}

            <div className="mt-2 pt-2 border-t border-slate-100">
              <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">{t("nav.more") || "More"}</div>
              {link("/services", t("nav.services") || "Services", "mobile-nav-services")}
              {link("/about", t("nav.about") || "About", "mobile-nav-about")}
              {link("/contact", t("nav.contact"), "mobile-nav-contact")}
              {link("/blog", t("nav.blog"), "mobile-nav-blog")}
            </div>
            <button
              onClick={() => setLang(lang === "en" ? "hi" : "en")}
              className="text-left px-3 py-2 text-sm font-medium text-slate-600 flex items-center gap-2"
              data-testid="mobile-lang-toggle"
            >
              <Globe className="w-4 h-4" /> {lang === "en" ? "हिंदी" : "English"}
            </button>
            {user ? (
              <>
                <Link to="/dashboard" className="px-3 py-2 text-sm font-medium text-emerald-700" data-testid="mobile-dashboard-link">{t("nav.dashboard")}</Link>
                <button onClick={() => { logout(); navigate("/"); }} className="text-left px-3 py-2 text-sm text-slate-500" data-testid="mobile-logout-btn">{t("nav.logout")}</button>
              </>
            ) : (
              <>
                <Link
                  to="/partner/register"
                  onClick={() => setOpen(false)}
                  className="px-3 py-2 text-sm font-medium text-slate-600 hover:text-emerald-700"
                  data-testid="mobile-become-partner-btn"
                >
                  {t("nav.becomePartner")}
                </Link>
                <div className="flex gap-2 px-3 pt-2">
                  <Link to="/login" className="nc-btn-secondary flex-1 text-sm py-2 justify-center" data-testid="mobile-login-btn">{t("nav.login")}</Link>
                  <Link to="/signup" className="nc-btn-primary flex-1 text-sm py-2 justify-center" data-testid="mobile-signup-btn">{t("nav.signup")}</Link>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
